import React, { useState, useRef, useEffect, useCallback } from "react";
import { speakText } from "../utils/speech";
import confetti from "canvas-confetti";
import GameSummary from "./GameSummary";
import TipsAndUpdatesIcon from '@mui/icons-material/TipsAndUpdates';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import HomeIcon from '@mui/icons-material/Home';
import DiamondIcon from '@mui/icons-material/Diamond';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

export default function ChineseSpellingView({ words, kidName, onExit }) {
    const [currentWordIndex, setCurrentWordIndex] = useState(0);
    const [userInput, setUserInput] = useState("");
    const [result, setResult] = useState("");
    const [score, setScore] = useState(0);
    const [attempts, setAttempts] = useState(0);
    const [showSummary, setShowSummary] = useState(false);
    const [tries, setTries] = useState(0);
    const [locked, setLocked] = useState(false);
    const [streak, setStreak] = useState(0);
    const [totalPoints, setTotalPoints] = useState(0);
    const [scorePopup, setScorePopup] = useState(null);
    const [maxStreak, setMaxStreak] = useState(0);
    const [personalBest, setPersonalBest] = useState(0);
    const [showSuccess, setShowSuccess] = useState(false);
    const [showWrongAnswer, setShowWrongAnswer] = useState(false);
    const [showHint, setShowHint] = useState(false);
    const [hintLevel, setHintLevel] = useState(0);

    const inputRef = useRef(null);
    const currentWord = words[currentWordIndex];

    const getPerfectScore = () => {
        let pts = 0;
        for (let i = 0; i < words.length; i++) {
            let m = i >= 10 ? 5 : i >= 7 ? 4 : i >= 5 ? 3 : i >= 3 ? 2 : 1;
            pts += 30 * m;
        }
        return pts;
    };

    useEffect(() => {
        const init = () => window.speechSynthesis.getVoices();
        init();
        window.speechSynthesis.onvoiceschanged = init;
        return () => { window.speechSynthesis.onvoiceschanged = null; };
    }, []);

    useEffect(() => {
        const key = `personalBest_chinese_${kidName}_${words.map(w => w.chinese).join('-').substring(0, 50)}`;
        const saved = localStorage.getItem(key);
        if (saved) setPersonalBest(parseInt(saved));
    }, [kidName, words]);

    const getStreakMultiplier = () =>
        streak >= 10 ? 5 : streak >= 7 ? 4 : streak >= 5 ? 3 : streak >= 3 ? 2 : 1;

    const calculatePoints = (attemptNumber) => {
        const base = attemptNumber === 1 ? 30 : attemptNumber === 2 ? 20 : 10;
        return base * getStreakMultiplier();
    };

    const speakWord = useCallback((index = currentWordIndex) => {
        if (!words[index]) return;
        speakText(words[index].chinese, 'zh', 0.8);
        setTimeout(() => inputRef.current?.focus(), 200);
    }, [words, currentWordIndex]);

    useEffect(() => {
        speakWord();
        setTimeout(() => inputRef.current?.focus(), 300);
        setShowHint(false);
        setHintLevel(0);
    }, [currentWordIndex, speakWord]);

    const playCorrectSound = () => {
        const ctx = new (window.AudioContext || window.webkitAudioContext)();
        [[523.25, 0], [659.25, 0.15], [783.99, 0.3], [1046.50, 0.45]].forEach(([f, d]) => {
            const o = ctx.createOscillator(), g = ctx.createGain();
            o.type = 'square'; o.frequency.setValueAtTime(f, ctx.currentTime + d);
            g.gain.setValueAtTime(0.2, ctx.currentTime + d);
            g.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + d + 0.15);
            o.connect(g); g.connect(ctx.destination);
            o.start(ctx.currentTime + d); o.stop(ctx.currentTime + d + 0.15);
        });
    };

    const playWrongSound = () => {
        const ctx = new (window.AudioContext || window.webkitAudioContext)();
        [[400, 0], [350, 0.1], [250, 0.2]].forEach(([f, d]) => {
            const o = ctx.createOscillator(), g = ctx.createGain();
            o.type = 'sawtooth'; o.frequency.setValueAtTime(f, ctx.currentTime + d);
            g.gain.setValueAtTime(0.2, ctx.currentTime + d);
            g.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + d + 0.2);
            o.connect(g); g.connect(ctx.destination);
            o.start(ctx.currentTime + d); o.stop(ctx.currentTime + d + 0.2);
        });
    };

    const savePersonalBest = (points) => {
        if (!kidName) return false;
        const key = `personalBest_chinese_${kidName}_${words.map(w => w.chinese).join('-').substring(0, 50)}`;
        const existing = parseInt(localStorage.getItem(key)) || 0;
        if (points > existing) { localStorage.setItem(key, points.toString()); setPersonalBest(points); return true; }
        return false;
    };

    const normalizePinyin = (p) => p.toLowerCase()
        .replace(/[āáǎà]/g, 'a').replace(/[ēéěè]/g, 'e')
        .replace(/[īíǐì]/g, 'i').replace(/[ōóǒò]/g, 'o')
        .replace(/[ūúǔù]/g, 'u').replace(/[ǖǘǚǜü]/g, 'v')
        .replace(/\s+/g, '').trim();

    const handleSubmit = () => {
        if (locked || words.length === 0 || !userInput.trim() || showWrongAnswer || showSuccess) return;
        const correctPinyin = normalizePinyin(currentWord.pinyin);
        const userPinyin = normalizePinyin(userInput);
        const nextAttempts = attempts + 1;
        const nextTries = tries + 1;

        if (userPinyin === correctPinyin) {
            const nextScore = score + 1;
            const earnedPoints = calculatePoints(nextTries);
            const nextTotalPoints = totalPoints + earnedPoints;
            const nextStreak = streak + 1;
            setShowSuccess(true);
            setScorePopup({ points: earnedPoints, x: 50, y: 30 });
            setTimeout(() => setScorePopup(null), 2000);
            setResult("Correct!");
            setScore(nextScore);
            setTotalPoints(nextTotalPoints);
            setStreak(nextStreak);
            setMaxStreak(Math.max(maxStreak, nextStreak));
            setAttempts(nextAttempts);
            setTries(0);
            playCorrectSound();
            const animationEnd = Date.now() + 2000;
            const frame = () => {
                confetti({ particleCount: 3, angle: 55 + Math.random() * 70, spread: 50 + Math.random() * 20, origin: { x: Math.random(), y: Math.random() - 0.2 }, colors: ['#4caf50', '#ffd700', '#2196f3'], gravity: 0.6 });
                if (Date.now() < animationEnd) requestAnimationFrame(frame);
            };
            frame();
            if (currentWordIndex + 1 < words.length) {
                setTimeout(() => { setShowSuccess(false); setCurrentWordIndex(currentWordIndex + 1); setUserInput(""); setResult(""); setLocked(false); }, 2000);
            } else {
                setTimeout(() => {
                    setShowSuccess(false);
                    if (savePersonalBest(nextTotalPoints)) setTimeout(() => confetti({ particleCount: 200, spread: 100, origin: { y: 0.6 } }), 500);
                    setShowSummary(true);
                }, 2000);
            }
        } else {
            playWrongSound();
            setAttempts(nextAttempts);
            setTries(nextTries);
            setStreak(0);
            setShowWrongAnswer(true);
            if (nextTries >= 3) {
                setResult(`The correct pinyin is: "${currentWord.pinyin}"`);
                setLocked(true);
                setTimeout(() => {
                    setShowWrongAnswer(false);
                    if (currentWordIndex + 1 < words.length) { setCurrentWordIndex(currentWordIndex + 1); setUserInput(""); setResult(""); setTries(0); setLocked(false); }
                    else setShowSummary(true);
                }, 2000);
            } else {
                setResult(`Try again! (${nextTries}/3)`);
                setTimeout(() => setShowWrongAnswer(false), 2000);
            }
        }
        setTimeout(() => inputRef.current?.focus(), 100);
    };

    const getHintText = () => {
        const p = currentWord.pinyin;
        if (hintLevel === 1) return `Hint: The pinyin has ${p.length} character${p.length !== 1 ? 's' : ''}`;
        if (hintLevel === 2) return `Hint: It starts with "${p[0]}"`;
        if (hintLevel === 3) return `Hint: It starts with "${p[0]}" and ends with "${p[p.length - 1]}"`;
        if (hintLevel === 4) { const h = Math.ceil(p.length / 2); return `Hint: ${p.substring(0, h)}${'_'.repeat(p.length - h)}`; }
        return '';
    };

    const handleNext = () => {
        if (currentWordIndex + 1 < words.length) {
            setCurrentWordIndex(currentWordIndex + 1);
            setUserInput(""); setResult(""); setTries(0); setLocked(false); setStreak(0);
        } else setShowSummary(true);
    };

    if (showSummary) {
        return (
            <div className="game-container">
                <GameSummary
                    kidName={kidName}
                    totalPoints={totalPoints}
                    personalBest={personalBest}
                    perfectScore={getPerfectScore()}
                    maxStreak={maxStreak}
                    score={score}
                    totalWords={words.length}
                    attempts={attempts}
                    leaderboard={[]}
                    onExit={onExit}
                />
            </div>
        );
    }

    return (
        <div className="game-container">
            <div className="game-header">
                <span className="game-progress-label">Word {currentWordIndex + 1} / {words.length}</span>
                <button onClick={onExit} className="btn btn-link">
                    <HomeIcon className="icon-sm" /> Exit
                </button>
            </div>

            {showWrongAnswer && (
                <div className="modal-backdrop">
                    <div className="game-modal game-modal-wrong">
                        <div className="game-modal-icon wrong"><CloseIcon className="icon-xl" /></div>
                        <div className="game-modal-title">OOPS!</div>
                        <div className="game-modal-message">
                            {tries >= 3 ? 'No more tries!' : `Try again! ${3 - tries} ${3 - tries === 1 ? 'try' : 'tries'} left`}
                        </div>
                        {tries >= 3 && (
                            <>
                                <div className="game-modal-label">The correct pinyin is:</div>
                                <div className="game-modal-word">{currentWord?.pinyin}</div>
                            </>
                        )}
                    </div>
                </div>
            )}

            {showSuccess && (
                <div className="modal-backdrop">
                    <div className="game-modal game-modal-success">
                        <div className="game-modal-icon success"><CheckIcon className="icon-xl" /></div>
                        <div className="game-modal-title">CORRECT!</div>
                        {scorePopup && <div className="game-modal-points">+{scorePopup.points} pts</div>}
                    </div>
                </div>
            )}

            {scorePopup && (
                <div className="floating-score" style={{ top: `${scorePopup.y}%`, left: `${scorePopup.x}%` }}>
                    +{scorePopup.points}
                </div>
            )}

            <div className="game-content">
                {/* Compact stats bar */}
                <div className="sp-stats-bar">
                    <div className="sp-stat">
                        <LocalFireDepartmentIcon style={{ fontSize: 16, color: '#ef4444' }} />
                        <span>{streak}{streak >= 3 && ` ×${getStreakMultiplier()}`}</span>
                    </div>
                    <div className="sp-stat sp-score">
                        {score} / {words.length}
                    </div>
                    <div className="sp-stat">
                        <DiamondIcon style={{ fontSize: 16, color: '#3b82f6' }} />
                        <span>{totalPoints}</span>
                    </div>
                </div>

                {/* Chinese character prompt card */}
                <div className="sp-chinese-card">
                    <div className="sp-chinese-char">{currentWord?.chinese}</div>
                    {currentWord?.english && (
                        <div className="sp-chinese-english">{currentWord.english}</div>
                    )}
                    <button className="sp-play-mini" onClick={() => speakWord()}>
                        <VolumeUpIcon style={{ fontSize: 20 }} />
                    </button>
                </div>

                {/* Hint */}
                {!locked && (
                    <div className="game-hint-section">
                        <button
                            onClick={() => { if (hintLevel < 4) { setHintLevel(hintLevel + 1); setShowHint(true); } }}
                            className={`btn btn-ghost btn-block${hintLevel >= 4 ? ' disabled' : ''}`}
                            disabled={hintLevel >= 4}
                            style={{ fontSize: 13 }}
                        >
                            <TipsAndUpdatesIcon className="icon-sm" />
                            {hintLevel === 0 ? 'Show Hint' : hintLevel >= 4 ? 'No More Hints' : `More Hints (${hintLevel}/4)`}
                        </button>
                        {showHint && hintLevel > 0 && <div className="game-hint-box">{getHintText()}</div>}
                    </div>
                )}

                {/* Input */}
                <div className="game-input-section">
                    <input
                        ref={inputRef}
                        type="text"
                        className={`game-input${locked ? ' disabled' : ''}`}
                        value={userInput}
                        onChange={(e) => setUserInput(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter' && !locked) { e.preventDefault(); handleSubmit(); } }}
                        disabled={locked}
                        placeholder="Type the pinyin here..."
                        autoComplete="off"
                    />
                </div>

                {!locked ? (
                    <button onClick={handleSubmit} className="btn btn-success btn-lg btn-block">
                        <CheckIcon className="icon-md" /> Check Answer
                    </button>
                ) : (
                    <button onClick={handleNext} className="btn btn-primary btn-lg btn-block">
                        <ArrowForwardIcon className="icon-md" /> Next Word
                    </button>
                )}

                {result && (
                    <div className={`game-result ${result.includes('Correct') ? 'success' : 'error'}`}>
                        {result}
                    </div>
                )}

                <div className="game-spacer" />
            </div>
        </div>
    );
}
